import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// ── Mobile Device Document ──

export interface MobileDeviceDocument {
  id: string;
  tenant_id: string;
  user_id: string;
  device_name: string;
  platform: string;
  os_version: string;
  app_version: string;
  push_token: string | null;
  push_provider: string;
  device_fingerprint: string;
  is_active: boolean;
  last_login_at: string | null;
  last_sync_at: string | null;
  ip_address: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface RegisterMobileDeviceRequest {
  device_name: string;
  platform: string;
  os_version: string;
  app_version: string;
  push_token?: string;
  push_provider?: string;
  device_fingerprint: string;
  notes?: string;
}

export interface UpdateMobileDeviceRequest {
  device_name?: string;
  os_version?: string;
  app_version?: string;
  push_token?: string;
  push_provider?: string;
  is_active?: boolean;
  notes?: string;
}

export interface SyncMobileDeviceRequest {
  push_token?: string;
  app_version?: string;
}

export interface MobileDeviceFilters extends SearchParams {
  user_id?: string;
  platform?: string;
  is_active?: string;
}

// ── Mobile Dashboard ──

export interface MobileActivitySummary {
  id: string;
  type: string;
  subject: string;
  due_date: string | null;
  status: string;
  created_at: string;
}

export interface MobilePipelineStageSummary {
  stage_id: string;
  count: number;
  total_value: number;
}

export interface MobileDashboardResponse {
  total_customers: number;
  total_opportunities: number;
  open_tickets: number;
  unread_notifications: number;
  upcoming_activities: number;
  recent_activities: MobileActivitySummary[];
  pipeline_summary: MobilePipelineStageSummary[];
  server_time: string;
}

// ── Mobile Quick Search ──

export interface MobileSearchHit {
  id: string;
  type: string;
  title: string;
  subtitle: string;
}

export interface MobileQuickSearchResponse {
  customers: MobileSearchHit[];
  contacts: MobileSearchHit[];
  opportunities: MobileSearchHit[];
  total_customers: number;
  total_contacts: number;
  total_opportunities: number;
}

// ── Constants ──

export const MOBILE_PLATFORMS = ['android', 'ios', 'windows', 'other'] as const;
export const MOBILE_PUSH_PROVIDERS = ['fcm', 'apns', 'none'] as const;

// ── Device API ──

export const getMobileDevices = (params: MobileDeviceFilters = {}) =>
  apiGet<PagedResponse<MobileDeviceDocument>>('/api/v1/mobile/devices', params as Record<string, unknown>);

export const getMobileDevice = (id: string) =>
  apiGet<MobileDeviceDocument>(`/api/v1/mobile/devices/${id}`);

export const registerMobileDevice = (data: RegisterMobileDeviceRequest) =>
  apiPost<MobileDeviceDocument>('/api/v1/mobile/devices', data);

export const updateMobileDevice = (id: string, data: UpdateMobileDeviceRequest) =>
  apiPut<MobileDeviceDocument>(`/api/v1/mobile/devices/${id}`, data);

export const deactivateMobileDevice = (id: string) =>
  apiDelete(`/api/v1/mobile/devices/${id}`);

export const getMyDevices = () =>
  apiGet<PagedResponse<MobileDeviceDocument>>('/api/v1/mobile/devices/my-devices');

export const syncMobileDevice = (id: string, data?: SyncMobileDeviceRequest) =>
  apiPost<MobileDeviceDocument>(`/api/v1/mobile/devices/${id}/sync`, data ?? {});

// ── Aggregation API ──

export const getMobileDashboard = () =>
  apiGet<MobileDashboardResponse>('/api/v1/mobile/dashboard');

export const mobileQuickSearch = (q: string, size?: number) =>
  apiGet<MobileQuickSearchResponse>('/api/v1/mobile/search', { q, size } as Record<string, unknown>);
